/* global $, Modernizr */

import debounce from '../helpers/debounce';
import Awesomplete from '../vendor/awesomplete.custom';

import renderItem from './search/renderItem';
import getSearch from './search/getSearch';
import addLabels from './search/addLabels';

const $search = $('.js-search');
const SEARCH_INPUT_SELECTOR = '.js-search-input';
const SEARCH_SUBMIT_SELECTOR = '.js-search-submit';
const SEARCH_CLOSE_SELECTOR = '.js-search-close';

const BREAKPOINT = 768;
const SEARCH_FOCUS_DELAY = 300;

const KEY_LEFT = 37;
const KEY_RIGHT = 39;
const KEY_DOWN = 40;
const KEY_UP = 38;
const KEY_SHIFT = 16;

const DEBOUNCE_LENGTH = 100;

const IGNORED_KEYS = [KEY_UP, KEY_DOWN, KEY_RIGHT, KEY_LEFT, KEY_SHIFT];

const search = {
  initialize() {
    if ($search.length) {
      this.createEvents();
    }
  },

  createEvents() {
    if (Modernizr.mq(`(min-width: ${BREAKPOINT}px)`)) {
      $search.on('click', SEARCH_SUBMIT_SELECTOR, (e) => {
        const $target = $(e.currentTarget);
        const input = $target.parent().find(SEARCH_INPUT_SELECTOR);
        const isOpen = $search.hasClass('is-open');

        if (!isOpen) {
          e.preventDefault();
        }

        this.toggleSearch(input, isOpen);
      });

      $search.on('click', SEARCH_CLOSE_SELECTOR, () => {
        const isOpen = $search.hasClass('is-open');

        this.toggleSearch(null, isOpen);
      });
    }

    $(SEARCH_INPUT_SELECTOR).each((i, input) => {
      this.initializeAutocomplete(input);
    });
  },

  toggleSearch(input, isOpen) {
    if (!isOpen) {
      setTimeout(
        () => {
          if (input) {
            input.focus();
          }
        },
        SEARCH_FOCUS_DELAY,
      );
    }

    $search
      .toggleClass('is-open')
      .css('width', isOpen ? $(SEARCH_SUBMIT_SELECTOR).width() : `${$search.data('expanded-width')}px`);
  },

  initializeAutocomplete(searchInput) {
    const awesompleteInst = new Awesomplete(searchInput, {
      maxItems: 24,

      minChars: 4,

      // The list will come from the server, so leave it empty for now
      list: [],

      /**
       * Filter the items based on the user input, we will always return true, because
       * the data is already filtered on the server
       * @param  {Object} text  Data from the API
       * @param  {String} input User input
       * @return {Boolean}      True if it should pass, and false if not
       */
      filter: () => true,

      /**
       * Sort on the group/category
       * @param  {Object} a Item to compare to b
       * @param  {Object} b Item to compare to a
       * @return {Number}   What the sort should do
       */
      sort: function sort() {
        return 0;
      },

      /**
       * Render item with data
       * @param  {Object} text  Data to show
       * @param  {String} input User input
       * @return {Node}         Node that will render to the DOM
       */
      item: (text, input) => {
        const html = (input === '')
          ? text
          : text.replace(RegExp(input.trim(), 'gi'), '<mark>$&</mark>');

        const cat = text.value.group;

        return renderItem(html, cat);
      },

      /**
       * Rewrite data stream to Awesomplete data object
       * @param  {Object} item Object from the API
       * @return {Object}      Awesomplete compatible data object
       */
      data: (item) => {
        const obj = {
          label: item.label,
          value: {
            group: item.group,
          },
        };

        if (typeof item.direct_url !== 'undefined') {
          obj.value.url = item.direct_url;
        }

        return obj;
      },

      replace: (text) => {
        // If a url is defined, we do a immediate redirect
        if (typeof text.value.url !== 'undefined') {
          window.location = text.value.url;
        } else {
          $(search).val(text.label);
          $(search).parents('form').submit();
        }
      },
    });

    /**
     * Custom event listener from Awesomplete, that is called when the list items are rendered
     */
    searchInput.addEventListener('awesomplete-rendered', (e) => {
      addLabels(e.el);
    });

    searchInput.addEventListener('awesomplete-open', () => {
      $(SEARCH_INPUT_SELECTOR).addClass('has-data');
    });

    searchInput.addEventListener('awesomplete-close', () => {
      $(SEARCH_INPUT_SELECTOR).removeClass('has-data');
    });

    // Add a slight debounce, to not overflow the server, and have crossing requests
    const searchDebounced = debounce(getSearch, DEBOUNCE_LENGTH);

    // Listen for keyups on the input field, and request new items
    $(searchInput).on('keyup', (e) => {
      if (IGNORED_KEYS.indexOf(e.keyCode) >= 0) {
        return true;
      }

      if (e.currentTarget.value.length <= 3) {
        return true;
      }

      searchDebounced(e.currentTarget.value, (data) => {
        // Apply the list to Awesomplete
        awesompleteInst.list = data;

        // Reevaluate the items and suggestions
        awesompleteInst.evaluate();
      });

      return true;
    });
  },
};

export default search;
